import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Clock, X } from 'lucide-react';
import clsx from 'clsx';

/**
 * Single-shot replacement for `<input type="datetime-local">`. Renders a
 * trigger styled like our other inputs, and opens a custom popover with a
 * month grid and 24h hour/minute scrollers.
 *
 * Value format: 'YYYY-MM-DDTHH:mm' (matches datetime-local), so existing
 * call sites that pass it straight to `start_time` / `end_time` keep working.
 */
interface Props {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
}

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const WEEKDAYS = ['M','T','W','T','F','S','S'];

function pad(n: number): string { return n < 10 ? `0${n}` : String(n); }
function parseValue(v: string): { date: Date | null; hour: number; minute: number } {
  if (!v) return { date: null, hour: 9, minute: 0 };
  const [datePart, timePart = '09:00'] = v.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  if (!y || !m || !d) return { date: null, hour: 9, minute: 0 };
  return { date: new Date(y, m - 1, d), hour: hh ?? 9, minute: mm ?? 0 };
}
function formatValue(date: Date, hour: number, minute: number): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hour)}:${pad(minute)}`;
}
function formatDisplay(date: Date | null, hour: number, minute: number): string {
  if (!date) return '';
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()} · ${h12}:${pad(minute)} ${ampm}`;
}

function startOfMonthGrid(view: Date): Date[] {
  // Returns 6 × 7 dates, Mon-first. Days before/after the month are still
  // included so the grid is uniform.
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const dayOfWeek = (first.getDay() + 6) % 7; // Mon=0
  const start = new Date(first);
  start.setDate(first.getDate() - dayOfWeek);
  const out: Date[] = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(d);
  }
  return out;
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export const DateTimePicker: React.FC<Props> = ({ value, onChange, disabled, placeholder = 'Pick a date & time', minDate }) => {
  const parsed = useMemo(() => parseValue(value), [value]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<Date>(parsed.date ?? new Date());
  const [hour, setHour] = useState<number>(parsed.hour);
  const [minute, setMinute] = useState<number>(parsed.minute);
  const ref = useRef<HTMLDivElement>(null);

  // Re-sync when external value changes
  useEffect(() => {
    setHour(parsed.hour);
    setMinute(parsed.minute);
    if (parsed.date) setView(parsed.date);
  }, [parsed.date?.getTime(), parsed.hour, parsed.minute]);

  // Click-outside to close
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const grid = useMemo(() => startOfMonthGrid(view), [view]);

  const pickDate = (d: Date) => {
    if (minDate && d.getTime() < new Date(minDate.toDateString()).getTime()) return;
    onChange(formatValue(d, hour, minute));
  };
  const pickHour = (h: number) => { onChange(formatValue(parsed.date ?? new Date(), h, minute)); };
  const pickMinute = (m: number) => { onChange(formatValue(parsed.date ?? new Date(), hour, m)); };
  const clear = () => { onChange(''); setOpen(false); };
  const today = () => {
    const d = new Date();
    setView(d);
    onChange(formatValue(d, hour, minute));
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(v => !v)}
        className={clsx(
          'flex h-9 w-full items-center justify-between rounded-lg border border-border bg-surface px-3 text-sm text-text-primary transition-colors',
          'hover:border-border-strong focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        <span className={clsx(parsed.date ? 'text-text-primary' : 'text-text-muted')}>
          {parsed.date ? formatDisplay(parsed.date, hour, minute) : placeholder}
        </span>
        <Calendar size={14} className="text-text-muted" />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-[320px] overflow-hidden rounded-xl border border-border bg-surface shadow-elevated">
          {/* Header: month nav */}
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <button
              type="button"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))}
              className="rounded-md p-1 text-text-muted hover:bg-surface-alt hover:text-text-primary"
            ><ChevronLeft size={15} /></button>
            <span className="text-sm font-semibold text-text-primary">
              {MONTHS[view.getMonth()]} {view.getFullYear()}
            </span>
            <button
              type="button"
              onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))}
              className="rounded-md p-1 text-text-muted hover:bg-surface-alt hover:text-text-primary"
            ><ChevronRight size={15} /></button>
          </div>

          {/* Calendar grid */}
          <div className="p-2">
            <div className="grid grid-cols-7 gap-0.5 text-center text-2xs font-semibold text-text-muted">
              {WEEKDAYS.map((d, i) => <div key={i} className="py-1">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {grid.map((d, i) => {
                const inMonth = d.getMonth() === view.getMonth();
                const isSelected = parsed.date ? sameDay(d, parsed.date) : false;
                const isToday = sameDay(d, new Date());
                const isDisabled = !!minDate && d.getTime() < new Date(minDate.toDateString()).getTime();
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => pickDate(d)}
                    className={clsx(
                      'h-8 rounded-md text-xs font-medium transition-colors',
                      !inMonth && 'text-text-muted/50',
                      inMonth && !isSelected && 'text-text-primary hover:bg-surface-alt',
                      isSelected && 'bg-primary text-on-primary',
                      isToday && !isSelected && 'ring-1 ring-primary/50',
                      isDisabled && 'cursor-not-allowed opacity-40',
                    )}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time scrollers + actions */}
          <div className="flex items-stretch border-t border-border">
            <div className="flex-1 grid grid-cols-2 gap-px bg-border">
              <Scroller label={<Clock size={11} />} value={hour} max={23} onChange={pickHour} format={(v) => pad(v)} />
              <Scroller label="" value={minute} max={59} step={5} onChange={pickMinute} format={(v) => pad(v)} />
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-border bg-surface-alt/40 px-3 py-2 text-xs">
            <button type="button" onClick={clear} className="flex items-center gap-1 text-text-muted hover:text-danger">
              <X size={11} /> Clear
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={today} className="text-primary hover:underline">Today</button>
              <button type="button" onClick={() => setOpen(false)} className="rounded-md bg-primary px-3 py-1 font-semibold text-on-primary hover:opacity-90">Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ScrollerProps {
  label: React.ReactNode;
  value: number;
  max: number;
  step?: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
}

const Scroller: React.FC<ScrollerProps> = ({ label, value, max, step = 1, format, onChange }) => {
  const items = useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i <= max; i += step) out.push(i);
    return out;
  }, [max, step]);

  return (
    <div className="bg-surface flex flex-col">
      {label && <div className="border-b border-border px-2 py-1 text-2xs text-text-muted">{label}</div>}
      <div className="max-h-32 overflow-y-auto">
        {items.map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={clsx(
              'flex h-7 w-full items-center justify-center text-xs font-medium transition-colors',
              v === value ? 'bg-primary-soft font-bold text-primary' : 'text-text-primary hover:bg-surface-alt',
            )}
          >
            {format(v)}
          </button>
        ))}
      </div>
    </div>
  );
};
